import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, TextField, useTheme } from '@mui/material';
import { ArrowDownward, ArrowUpward, Edit } from '@mui/icons-material';

import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { usePagination } from '@frontend/features/common/components/Table';
import { useGetMarkupRulesByAgencyId, useReorderMarkupRule } from '@frontend/features/markup-rules/queries';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';

import { routes } from '../routes';
import { useEffect, useState } from 'react';

interface OrderCellProps {
  ruleId: string;
  order: number;
  maxOrder: number;
  disabled: boolean;
  onMove: (ruleId: string, order: number) => void;
}

/** Flechas (±1) + input para tipear la posición (commit con Enter o blur). */
const OrderCell = ({ ruleId, order, maxOrder, disabled, onMove }: OrderCellProps) => {
  const [value, setValue] = useState(String(order));

  // Re-sincroniza cuando el reorder de otra fila renumera esta.
  useEffect(() => setValue(String(order)), [order]);

  const commit = () => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed === order) {
      setValue(String(order));
      return;
    }
    onMove(ruleId, Math.min(parsed, maxOrder));
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
      <IconButton
        size="small"
        disabled={disabled || order <= 1}
        onClick={() => onMove(ruleId, order - 1)}
      >
        <ArrowUpward fontSize="small" />
      </IconButton>
      <TextField
        size="small"
        value={value}
        disabled={disabled}
        onChange={(event) => setValue(event.target.value.replace(/\D/g, ''))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
        }}
        inputProps={{ style: { textAlign: 'center', padding: '4px 0' }, inputMode: 'numeric' }}
        sx={{ width: 48 }}
      />
      <IconButton
        size="small"
        disabled={disabled || order >= maxOrder}
        onClick={() => onMove(ruleId, order + 1)}
      >
        <ArrowDownward fontSize="small" />
      </IconButton>
    </Box>
  );
};


export const MarkupRulesList = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const theme = useTheme();
  const { id: agencyId } = useParams();
  const pagination = usePagination();
  const navigate = useNavigate();

  const { data: rulesList, isLoading } = useGetMarkupRulesByAgencyId({
    agencyId: Number(agencyId)
  });
  const reorder = useReorderMarkupRule();

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  const handleMove = (ruleId: string, order: number) =>
    reorder.mutate({ id: ruleId, order });

  return (
    <ListLayout padding={0} title="Markup rules" subtitle="Markup rules list">
      <ListContentLayout
        height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
        stickyHeader
        onSearchChange={handleOnSearch}
        onCreate={() =>
          agencyId && navigate(
            `${routes.dashboard.agencyView.root.replace(':id', agencyId)}/${routes.dashboard.agencyView.markupRulesView.replace(':ruleId', 'create')
            }`,
          )}
        columns={[
          {
            id: 'name',
            label: t('markupRules.name'),
            align: 'left',
          },
          {
            id: 'markup',
            label: t('markupRules.markup'),
            align: 'left',
          },
          {
            id: 'order',
            label: t('markupRules.order'),
            align: 'center',
          },
          { id: 'actions', label: '', align: 'right' },
        ]}
        isLoading={isLoading}

        data={{
          key: 'id',
          items: rulesList
            ? rulesList.items.map((elItem) => ({
              ...elItem,
              name: (
                <Link
                  to={`${routes.dashboard.agencyView.root.replace(':id', String(agencyId))}/${routes.dashboard.agencyView.markupRulesView.replace(':ruleId',elItem.id)
                  }`}
                >
                  {elItem.name}
                </Link>
              ),
              markup: `${elItem.value} ${elItem.type === 'percentage' ? '%' : t('markupRules.fee')}` ,
              order: (
                <OrderCell
                  ruleId={elItem.id}
                  order={elItem.order}
                  maxOrder={rulesList.count || rulesList.items.length}
                  disabled={reorder.isLoading}
                  onMove={handleMove}
                />
              ),
              actions: (
                <Box>
                  {agencyId && (
                    <IconButton
                      onClick={() =>
                        navigate(
                          `${routes.dashboard.agencyView.root.replace(':id', agencyId)}/${routes.dashboard.agencyView.markupRulesView.replace(':ruleId', elItem.id)
                          }`,
                        )
                      }
                    >
                      <Edit color="primary" />
                    </IconButton>
                  )}
                </Box>
              ),
            }))
            : [],
          totalCount: rulesList?.count || 0,
        }}
      />
    </ListLayout>
  );
};
